import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "../../components/ui/Button";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { EmptyState } from "../../components/ui/EmptyState";
import { ErrorText } from "../../components/ui/feedback";
import { LabelGrid } from "../../components/ui/LabelGrid";
import { PageHeader } from "../../components/ui/PageHeader";
import { useI18n } from "../../i18n/I18nProvider";
import { apiRequest } from "../../lib/api";
import type { Device, LinkCode } from "../../lib/api-types";
import { paths } from "../../lib/endpoints";
import { DeviceRow } from "./DeviceRow";
import { LinkCodePanel } from "./LinkCodePanel";

export interface DevicesPageProps {
  workspaceId: string;
}

const devicesQuery = (workspaceId: string) => ({
  queryKey: ["devices", workspaceId] as const,
  queryFn: () => apiRequest<Device[]>(paths.devices(workspaceId)),
});

/**
 * Desktop apps linked to this workspace. The operator makes a one-use code here, the desktop app
 * redeems it, and the device shows up in the list. Revoked devices are hidden.
 */
export function DevicesPage({ workspaceId }: DevicesPageProps) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const devices = useQuery(devicesQuery(workspaceId));
  const [linkCode, setLinkCode] = useState<LinkCode | null>(null);
  const [unlinking, setUnlinking] = useState<Device | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["devices", workspaceId] });

  const createCode = useMutation({
    mutationFn: () => apiRequest<LinkCode>(paths.deviceCodes(workspaceId), { method: "POST" }),
    onSuccess: (result) => setLinkCode(result),
  });

  const unlink = useMutation({
    mutationFn: (deviceId: string) =>
      apiRequest<unknown>(paths.device(workspaceId, deviceId), { method: "DELETE" }),
    onSuccess: () => {
      setUnlinking(null);
      return refresh();
    },
  });

  const closePanel = () => {
    setLinkCode(null);
    return refresh();
  };

  if (devices.error) return <ErrorText error={devices.error} />;
  if (!devices.data) return <p className="text-note text-ink-2">{t("app.loading")}</p>;

  const visible = devices.data.filter((device) => device.revoked_at === null);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("devices.title")}
        actions={
          linkCode ? null : (
            <Button
              variant="primary"
              disabled={createCode.isPending}
              onClick={() => createCode.mutate()}
            >
              {t("devices.linkButton")}
            </Button>
          )
        }
      />
      <p className="max-w-prose text-operate text-ink-2">{t("devices.intro")}</p>
      <ErrorText error={linkCode ? undefined : createCode.error} />

      {linkCode ? (
        <LinkCodePanel
          key={linkCode.code}
          link={linkCode}
          busy={createCode.isPending}
          error={createCode.error}
          onNewCode={() => createCode.mutate()}
          onDone={() => void closePanel()}
        />
      ) : null}

      {visible.length === 0 ? (
        <EmptyState>{t("devices.empty")}</EmptyState>
      ) : (
        <LabelGrid>
          {visible.map((device) => (
            <DeviceRow
              key={device.id}
              device={device}
              busy={unlink.isPending}
              onUnlink={() => setUnlinking(device)}
            />
          ))}
        </LabelGrid>
      )}
      <ErrorText error={unlink.error} />

      {unlinking ? (
        <ConfirmDialog
          title={t("devices.confirmUnlinkTitle")}
          message={t("devices.confirmUnlink", { name: unlinking.name })}
          confirmLabel={t("devices.unlink")}
          variant="danger"
          busy={unlink.isPending}
          error={unlink.error}
          onCancel={() => setUnlinking(null)}
          onConfirm={() => unlink.mutate(unlinking.id)}
        />
      ) : null}
    </div>
  );
}
